import React, { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import "./CustomRecurrenceModal.css";

const FREQUENCY_OPTIONS = [
  { value: "day", label: "day", plural: "days" },
  { value: "week", label: "week", plural: "weeks" },
  { value: "month", label: "month", plural: "months" },
  { value: "year", label: "year", plural: "years" },
];

const CustomRecurrenceModal = ({ isOpen, onClose, onSave, initialValue }) => {
  const [interval, setInterval] = useState(1);
  const [frequency, setFrequency] = useState("week");
  const [endType, setEndType] = useState("after");
  const [endDate, setEndDate] = useState("");
  const [occurrences, setOccurrences] = useState(13);

  // Initialize state from initialValue
  useEffect(() => {
    if (initialValue) {
      setInterval(initialValue.interval || 1);
      // Map "none" or invalid frequencies to a valid default
      const validFrequencies = ["day", "week", "month", "year"];
      const freq = initialValue.frequency;
      setFrequency(validFrequencies.includes(freq) ? freq : "week");
      setEndType(initialValue.endType || "after");
      setEndDate(initialValue.endDate || "");
      setOccurrences(initialValue.occurrences || 13);
    }
  }, [initialValue, isOpen]);

  // Set default end date to 3 months from now
  useEffect(() => {
    if (!endDate) {
      const defaultDate = new Date();
      defaultDate.setMonth(defaultDate.getMonth() + 3);
      setEndDate(defaultDate.toISOString().split("T")[0]);
    }
  }, [endDate]);

  const handleSave = () => {
    const rule = {
      frequency,
      interval,
      endType,
      endDate: endType === "on" ? endDate : null,
      occurrences: endType === "after" ? occurrences : (endType === "never" ? 52 : null),
    };
    onSave(rule);
    onClose();
  };

  const getFrequencyLabel = () => {
    const option = FREQUENCY_OPTIONS.find((f) => f.value === frequency);
    return interval === 1 ? option.label : option.plural;
  };

  if (!isOpen) return null;

  return (
    <div className="custom-recurrence-overlay" onClick={onClose}>
      <div className="custom-recurrence-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Custom recurrence</h2>
          <button className="close-button" onClick={onClose} type="button">
            <IoClose size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Repeat Every Section */}
          <div className="form-row">
            <label className="row-label">Repeat every</label>
            <div className="row-inputs">
              <input
                type="number"
                min="1"
                max="99"
                value={interval}
                onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
                className="interval-input"
              />
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="frequency-select"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {interval === 1 ? opt.label : opt.plural}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Ends Section */}
          <div className="form-row ends-section">
            <label className="row-label">Ends</label>
            <div className="ends-options">
              <label className="radio-option">
                <input
                  type="radio"
                  name="endType"
                  value="never"
                  checked={endType === "never"}
                  onChange={() => setEndType("never")}
                />
                <span className="radio-label">Never</span>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="endType"
                  value="on"
                  checked={endType === "on"}
                  onChange={() => setEndType("on")}
                />
                <span className="radio-label">On</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={endType !== "on"}
                  className="date-input"
                />
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="endType"
                  value="after"
                  checked={endType === "after"}
                  onChange={() => setEndType("after")}
                />
                <span className="radio-label">After</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={occurrences}
                  onChange={(e) => setOccurrences(Math.max(1, parseInt(e.target.value) || 1))}
                  disabled={endType !== "after"}
                  className="occurrences-input"
                />
                <span className="suffix-text">occurrences</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} type="button">
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} type="button">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomRecurrenceModal;
